import { state, set, subscribe } from '../../state/store.js';
import { formatPrice, escapeHtml, pluralUa } from '../../lib/format.js';
import { updateLine, removeLine, removeLines } from '../../lib/shopify.js';
import { retryCart } from '../../state/actions.js';

const $list = () => document.getElementById('cart-list');

/**
 * Inline notice above the cart lines, used when Shopify silently corrects a
 * quantity (it clamps to available stock and reports only a warning). Created
 * lazily and re-inserted after each render, since render() replaces innerHTML.
 */
let cartNotice = '';
function showCartNotice(message) {
  cartNotice = message || '';
  paintCartNotice();
}
function clearCartNotice() {
  cartNotice = '';
  paintCartNotice();
}
function paintCartNotice() {
  const list = $list();
  if (!list?.parentNode) return;
  let el = document.getElementById('cart-notice');
  if (!cartNotice) { el?.remove(); return; }
  if (!el) {
    el = document.createElement('div');
    el.id = 'cart-notice';
    el.className = 'cart-notice';
    list.parentNode.insertBefore(el, list);
  }
  el.textContent = cartNotice;
}

/**
 * A line the customer cannot buy: sold out with "continue selling when out of
 * stock" OFF. These arrive from the STOREFRONT cart — the item was in stock
 * when it was added and sold out before checkout — so the customer has no idea
 * anything is wrong until the pay button refuses. Mark them, exclude them from
 * the totals, and offer a one-click way out.
 */
export function isUnavailable(line) {
  return line?.sellable === false || line?.availableForSale === false;
}

/** A sellable line whose quantity now exceeds what's actually in stock. */
export function exceedsStock(line) {
  if (!line || isUnavailable(line) || line.backorder) return false;
  return typeof line.stock === 'number' && line.stock >= 0 && line.qty > line.stock;
}

export function blockedLines(lines = []) {
  return lines.filter((l) => isUnavailable(l) || exceedsStock(l));
}

/**
 * Banner above the cart when something in it blocks checkout. Rendered from
 * cart state (not from an error response) so the customer sees it the moment
 * the cart hydrates, rather than after clicking Сплатити.
 */
function paintBlockedBanner() {
  const list = $list();
  if (!list?.parentNode) return;
  const lines = state.cart?.lines || [];
  const dead = lines.filter(isUnavailable);
  const over = lines.filter(exceedsStock);
  let el = document.getElementById('cart-blocked');

  if (!dead.length && !over.length) { el?.remove(); return; }

  if (!el) {
    el = document.createElement('div');
    el.id = 'cart-blocked';
    el.className = 'cart-blocked';
    list.parentNode.insertBefore(el, list);
  }
  const parts = [];
  if (dead.length) {
    parts.push(dead.length === 1
      ? 'Один товар у кошику закінчився.'
      // Both the noun AND the verb have to agree with the count:
      // "21 товар закінчився" but "22 товари закінчилися".
      : `${dead.length} ${pluralUa(dead.length, 'товар', 'товари', 'товарів')} у кошику `
        + `${pluralUa(dead.length, 'закінчився', 'закінчилися', 'закінчилися')}.`);
  }
  if (over.length) {
    parts.push(over.length === 1
      ? 'Для одного товару на складі залишилось менше, ніж ви обрали.'
      : `Для ${over.length} ${pluralUa(over.length, 'товару', 'товарів', 'товарів')} на складі залишилось менше, ніж ви обрали.`);
  }
  el.innerHTML = `<span class="cart-blocked-text">${parts.join(' ')} Виправте це, щоб продовжити.</span>`
    + (dead.length ? '<button type="button" id="cart-drop-unavailable">Видалити недоступні</button>' : '');

  const dropBtn = document.getElementById('cart-drop-unavailable');
  if (dropBtn) {
    dropBtn.addEventListener('click', async () => {
      const shop   = state.shop?.shopifyDomain;
      const cartId = state.cart?.id;
      const ids = (state.cart?.lines || []).filter(isUnavailable).map((l) => l.lineKey).filter(Boolean);
      if (!ids.length) return;
      dropBtn.disabled = true;
      dropBtn.textContent = 'Видалення…';
      if (shop && cartId) {
        try {
          const updated = await removeLines({ shop, cartId, lineIds: ids });
          if (updated) set({ cart: { ...state.cart, ...updated } });
          return;
        } catch (err) {
          console.error('[cart] bulk remove failed', err);
          dropBtn.disabled = false;
          dropBtn.textContent = 'Видалити недоступні';
          // Tell the customer — a silently restored button reads as "nothing
          // happened". The listener is NOT once-only, so a retry works.
          showCartNotice('Не вдалося видалити товари. Спробуйте ще раз.');
          return;
        }
      }
      // Offline/dev fallback: drop them locally.
      const next = (state.cart.lines || []).filter((l) => !isUnavailable(l));
      set({ cart: { ...state.cart, lines: next, subtotal: next.reduce((a, x) => a + x.price * x.qty, 0) } });
    });
  }
}

// Per-line debounce timers for qty +/-. Rapid clicks coalesce into one
// server mutation with the final quantity.
const pendingQtyTimers = new Map();

function scheduleQtySync(lineKey, qty) {
  if (!lineKey) return;
  clearTimeout(pendingQtyTimers.get(lineKey));
  const tid = setTimeout(async () => {
    pendingQtyTimers.delete(lineKey);
    const shop   = state.shop?.shopifyDomain;
    const cartId = state.cart?.id;
    if (!shop || !cartId) return;
    try {
      const updated = await updateLine({ shop, cartId, lineId: lineKey, quantity: qty });
      if (updated) set({ cart: { ...state.cart, ...updated } });
    } catch (err) {
      console.error('[cart] updateLine failed', err);
      // 409 = Shopify clamped the quantity to what's actually in stock (it
      // reports this as a warning, not an error, and silently reduces the
      // line). Adopt the corrected cart so the optimistic quantity we painted
      // doesn't stay on screen, and tell the customer why it changed.
      if (err.status === 409) {
        if (err.cart) set({ cart: { ...state.cart, ...err.cart } });
        showCartNotice(err.message);
      }
    }
  }, 400);
  pendingQtyTimers.set(lineKey, tid);
}

/**
 * The cart could not be loaded at all. Replaces the line list with an explicit
 * Ukrainian error and, when a retry could help, a button that re-runs the load
 * in place. Never asks the customer to reload: readShopParams() has already
 * stripped cartToken from the URL, so a reload can lose the session entirely.
 */
function renderCartLoadError(root, cartError) {
  const storeName = state.shop?.storeName;
  const storeUrl = storeName
    ? (/^https?:\/\//.test(storeName) ? storeName : `https://${storeName}`).replace(/\/+$/, '')
    : null;

  if (cartError.permanent) {
    root.innerHTML = `
      <div class="cart-load-error">
        <p class="cart-load-error-title">Не вдалося знайти ваш кошик</p>
        <p class="cart-load-error-text">Можливо, він застарів. Поверніться до магазину та оформіть замовлення ще раз.</p>
        ${storeUrl ? `<a class="cart-load-error-btn" href="${escapeHtml(storeUrl)}/cart">Повернутись до кошика</a>` : ''}
      </div>`;
    return;
  }

  const busy = !!cartError.retrying;
  root.innerHTML = `
    <div class="cart-load-error">
      <p class="cart-load-error-title">Не вдалося завантажити ваш кошик</p>
      <p class="cart-load-error-text">Перевірте з'єднання з інтернетом і спробуйте ще раз.</p>
      <button type="button" class="cart-load-error-btn" id="cart-retry" ${busy ? 'disabled' : ''}>
        ${busy ? 'Завантаження…' : 'Спробувати ще раз'}
      </button>
    </div>`;

  const btn = document.getElementById('cart-retry');
  if (btn && !busy) {
    btn.addEventListener('click', async () => {
      // Guard against double-submits: retryCart flips ui.cartError.retrying,
      // which re-renders this panel in its disabled state.
      await retryCart();
    }, { once: true });
  }
}

function render() {
  const root = $list();
  if (!root) return;
  const lines = state.cart.lines;

  // A failed load outranks everything else — an empty line list here means
  // "we don't know what's in the cart", not "the cart is empty".
  const cartError = state.ui?.cartError;
  if (cartError) {
    document.getElementById('cart-blocked')?.remove();
    document.getElementById('cart-notice')?.remove();
    renderCartLoadError(root, cartError);
    return;
  }

  if (lines.length === 0) {
    root.innerHTML = `<div style="padding:14px 4px;color:var(--ink-3);font-size:14px">Ваш кошик порожній</div>`;
    document.getElementById('cart-blocked')?.remove();
    return;
  }

  root.innerHTML = lines.map((it, idx) => {
    const thumb = it.image
      ? `<img src="${escapeHtml(it.image)}" alt="${escapeHtml(it.name)}">`
      : `<div class="ph">${(it.placeholder || 'product').split('\n').map(escapeHtml).join('<br/>')}</div>`;
    // Only cap against a real, positive stock count. Backorder lines
    // ("continue selling when out of stock") report stock <= 0, often
    // negative — clamping against that would disable "+" forever.
    const hasCap = typeof it.stock === 'number' && it.stock > 0 && !it.backorder;
    const dead = isUnavailable(it);
    const over = exceedsStock(it);
    // "+" is capped by real stock, and always locked on an unbuyable line.
    const atCap = dead || (hasCap && it.qty >= it.stock);
    const stockNote = dead
      ? '<div class="cart-stock is-out">Немає в наявності — видаліть, щоб продовжити</div>'
      : over
        ? `<div class="cart-stock is-out">Залишилось лише ${it.stock} шт. — зменшіть кількість</div>`
        : (typeof it.stock === 'number' && it.stock <= 5 && it.stock > 0)
          ? `<div class="cart-stock">Залишилось ${it.stock} шт.</div>`
          : '';
    return `
      <div class="cart-item${dead ? ' is-unavailable' : ''}${over ? ' is-over-stock' : ''}">
        <div class="cart-thumb">${thumb}</div>
        <div class="cart-info">
          <p class="cart-name">${escapeHtml(it.name)}</p>
          ${it.variant ? `<div class="cart-variant">${escapeHtml(it.variant)}</div>` : ''}
          ${stockNote}
          <div class="cart-bottom">
            <div class="qty">
              <button type="button" data-act="dec" data-i="${idx}" ${dead ? 'disabled aria-disabled="true"' : ''}>−</button>
              <span class="v">${it.qty}</span>
              <button type="button" data-act="inc" data-i="${idx}" ${atCap ? 'disabled aria-disabled="true"' : ''}>+</button>
            </div>
            <div class="cart-price">${formatPrice(it.price * it.qty)}</div>
          </div>
          <button type="button" class="cart-remove" data-act="rm" data-i="${idx}" aria-label="Видалити">×</button>
        </div>
      </div>
    `;
  }).join('');

  paintBlockedBanner();

  root.querySelectorAll('button[data-act]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const i = +btn.dataset.i;
      const act = btn.dataset.act;
      const line = state.cart.lines[i];
      if (!line) return;
      // Any fresh interaction supersedes the previous stock notice; the
      // debounced sync re-raises it if the new quantity is also unavailable.
      clearCartNotice();

      const shop   = state.shop?.shopifyDomain;
      const cartId = state.cart?.id;
      const liveMode = !!(shop && cartId && line.lineKey);

      // --- Remove --------------------------------------------------------
      if (act === 'rm') {
        if (liveMode) {
          btn.disabled = true;
          try {
            const updated = await removeLine({ shop, cartId, lineId: line.lineKey });
            if (updated) set({ cart: { ...state.cart, ...updated } });
          } catch (err) {
            console.error('[cart] removeLine failed', err);
            btn.disabled = false;
          }
        } else {
          const next = state.cart.lines.filter((_, idx) => idx !== i);
          const subtotal = next.reduce((a, x) => a + x.price * x.qty, 0);
          set({ cart: { ...state.cart, lines: next, subtotal } });
        }
        return;
      }

      // --- Qty +/- -------------------------------------------------------
      let newQty = act === 'inc' ? line.qty + 1 : Math.max(1, line.qty - 1);
      // Hard cap at tracked stock so we never ask Shopify for more than what
      // the merchant actually has. Backorder lines are exempt: they report
      // stock <= 0 by design, and clamping to it would drive the quantity
      // negative (Shopify then rejects the mutation outright).
      const hasCap = typeof line.stock === 'number' && line.stock > 0 && !line.backorder;
      if (hasCap && newQty > line.stock) {
        newQty = line.stock;
      }
      // Belt-and-braces: a cart line quantity below 1 is never valid.
      if (newQty < 1) newQty = 1;
      if (newQty === line.qty) return;

      // Optimistic local update for instant visual feedback. Totals.js
      // re-renders from state.cart.lines so the qty / line-total / subtotal
      // change immediately; the discount line refreshes when the server
      // response lands ~400ms later.
      const next = state.cart.lines.map((l, idx) => idx === i ? { ...l, qty: newQty } : l);
      const subtotal = next.reduce((a, x) => a + x.price * x.qty, 0);
      set({ cart: { ...state.cart, lines: next, subtotal } });

      if (liveMode) scheduleQtySync(line.lineKey, newQty);
    });
  });
}

export function initCart() {
  render();
  // 'ui' too: state.ui.cartError drives the load-failure panel.
  subscribe(['cart', 'ui'], render);
}
